import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Image,
  Text,
  VStack,
  Button,
  HStack,
  Icon,
  IconButton,
  Flex,
  useColorMode,
  useBreakpointValue,
  Tooltip,
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  ArrowForwardIcon,
  StarIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
import { FaHeart, FaRegHeart, FaCaravan } from "react-icons/fa"; // Import FaCaravan
import axiosInstance from "../../helpers/axiosInstance";
import Translate from "../../components/LanguageTranslation/Translate";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";
import ExternalLinkButton from "../ExternalLinkButton";

const RVRentalCard = ({
  RVs,
  location,
  category = "transportation",
  itinerary = false,
  conversationId,
  cityName,
  onRVsChange,
  fromItineray,
  fromChatItineray,
}) => {
  // console.log("RVRentalCard", RVs);
  const { convertCurrency, currency } = useCurrency();
  const { colorMode } = useColorMode();
  const [current, setCurrent] = useState(0);
  const [likedActivities, setLikedActivities] = useState({});
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isDeleting, setIsDeleting] = useState({});

  const numVisibleCards = useBreakpointValue({ base: 1, md: 2, lg: 2 });
  const numTotalCards = RVs?.length;
  // Check if RVs is an array and has items, or set it to an empty array
  const validRVs = Array.isArray(RVs) ? RVs : [];

  const containerRef = useRef(null);
  const touchStartXRef = useRef(null);

  const [showCallout, setShowCallout] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setShowCallout(false), 9000);
    return () => clearTimeout(timer);
  }, []); // run once on mount

  const next = () => {
    setCurrent((prev) => (prev + numVisibleCards) % numTotalCards);
  };

  const prev = () => {
    setCurrent(
      (prev) => (prev - numVisibleCards + numTotalCards) % numTotalCards
    );
  };

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartXRef.current - touchEndX;
    if (diff > 50) next();
    else if (diff < -50) prev();
  };

  const hasPrev = current > 0;
  const hasNext = current + numVisibleCards < numTotalCards;

  const isActivityLiked = (idx, rv) =>
    likedActivities[idx] ?? rv.added_to_bucket_list ?? false;

  const sendLikedActivity = async (rv, added) => {
    const payload = {
      conversation_data: rv,
      // category: category,
      conversation_type: "transportation_conversation",
      added_to_bucket_list: added,
    };
    try {
      await axiosInstance.post(
        `/chat/conversation/update-conversation-heart-icon/${conversationId}`,
        payload
      );
      // Call the second endpoint only when RV is liked
      if (added) {
        const bucketPayload = {
          data: rv,
          category: category,
          conversation_type: "transportation_conversation",
        };
        await axiosInstance.post(
          "/bucket-list/save-third-party-service",
          bucketPayload
        );
      }
      // console.log(`Activity ${isLiked ? "liked" : "unliked"}:`, rv);
    } catch (error) {
      console.error("Error saving liked activity:", error);
    }
  };

  // const toggleLike = (index, rv) => {
  //   const updatedLikes = { ...likedActivities };
  //   const isLiked = !updatedLikes[index];

  //   if (isLiked) {
  //     updatedLikes[index] = rv; // Add activity if liked
  //   } else {
  //     delete updatedLikes[index]; // Remove activity if unliked
  //   }

  //   setLikedActivities(updatedLikes);

  //   // Send only the current activity to backend
  //   sendLikedActivity(rv, isLiked);
  // };

  const toggleLike = (index, rv) => {
    const currentlyLiked = isActivityLiked(index, rv);
    const newLiked = !currentlyLiked; // white → red  OR  red → white

    setLikedActivities((prev) => ({ ...prev, [index]: newLiked }));
    sendLikedActivity(rv, newLiked); // <-- pass TRUE or FALSE
  };

  // Delete a specific RV
  const handleDeleteRV = async (rvIndex) => {
    if (!conversationId || !cityName) return;
    setIsDeleting((prev) => ({ ...prev, [rvIndex]: true }));
    try {
      await axiosInstance.delete(
        `/chat/conversation/delete-rv-info/${conversationId}`,
        {
          data: {
            city_name: cityName,
            rv_index: rvIndex,
          },
        }
      );
      if (onRVsChange) onRVsChange(rvIndex, "single");
    } catch (error) {
      console.error("Error deleting RV:", error);
    } finally {
      setIsDeleting((prev) => ({ ...prev, [rvIndex]: false }));
    }
  };

  return (
    <Flex
      position="relative"
      alignItems="center"
      justifyContent="center"
      padding={0}
      mt={3}
      width="100%"
    >
      {hasPrev && (
        <IconButton
          icon={<ArrowBackIcon />}
          isRound={true}
          size="md"
          onClick={prev}
          position="absolute"
          left={{ base: "1px", md: "10px" }}
          backgroundColor="white"
          boxShadow="base"
          zIndex="overlay"
        />
      )}
      <HStack
        ref={containerRef}
        overflow="hidden"
        width="full"
        h="auto"
        spacing={3}
        justifyContent={{ base: "center", md: "space-between" }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {RVs?.slice(current, current + numVisibleCards).map((rv, index) => {
          const convertedPrice = convertCurrency(rv?.price_per_day, "USD");
          const actualIndex = current + index;
          const isLiked = likedActivities[actualIndex] !== undefined;
          const liked = isActivityLiked(actualIndex, rv);
          return (
            <Box
              key={index}
              bg="gray.100"
              padding={2}
              borderRadius="xl"
              width={{ base: "80%", md: "50%", lg: "50%" }}
              // onClick={() =>
              //   onActivityClick &&
              //   onActivityClick(RVs.attractionLatitude, RVs.attractionLongitude)
              // }
              display="flex"
              flexDirection="column"
              alignItems="flex-start" // Align content to the left
            >
              <HStack spacing={2} mb={1} align="left">
                {" "}
                {/* Align HStack content to the left */}
                <Icon
                  as={FaCaravan}
                  color="#286328"
                  boxSize={4}
                  alignSelf="center"
                />{" "}
                {/* FaCaravan icon */}
                <Text
                  fontWeight="bold"
                  fontSize="sm"
                  mb={0}
                  noOfLines={1}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <Translate text="RVs Rental" />
                </Text>
              </HStack>
              <Box
                borderTopRadius="md"
                boxShadow="md"
                width="100%"
                position="relative"
              >
                <Image
                  src={
                    rv?.image_url ||
                    "https://via.placeholder.com/300x200.png?text=No+Image"
                  }
                  fallbackSrc="https://via.placeholder.com/300x200.png?text=No+Image"
                  alt={rv?.vehicle_name || "RV"}
                  borderTopRadius="xl"
                  objectFit="cover"
                  width="100%"
                  height="200px"
                />
                {/* Delete and Heart icons */}

                <Box
                  position="absolute"
                  top="3px"
                  right="3px"
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  gap="0px"
                >
                  {" "}
                  {fromChatItineray && (
                    <Tooltip label="Delete RV">
                      <IconButton
                        aria-label="Delete RV"
                        icon={<DeleteIcon />}
                        size="md"
                        color="black"
                        isLoading={isDeleting[actualIndex]}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleDeleteRV(actualIndex);
                        }}
                        borderRadius="full"
                        bg="none"
                        _hover={{ bg: "none" }}
                      />
                    </Tooltip>
                  )}
                  {!fromItineray && !fromChatItineray && (
                    <Tooltip
                      label={
                        liked ? "Remove from bucket list" : "Add to bucket list"
                      }
                    >
                      <IconButton
                        aria-label={
                          liked
                            ? "Remove from bucket list"
                            : "Add to bucket list"
                        }
                        icon={liked ? <FaHeart /> : <FaRegHeart />}
                        size="md"
                        color={liked ? "red.500" : "white"}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleLike(actualIndex, rv);
                        }}
                        borderRadius="full"
                        bg="none"
                        _hover={{ bg: "none" }}
                        marginLeft="-12px"
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
              <VStack
                alignItems="flex-start"
                width="100%"
                p={3}
                spacing={2}
                textAlign="left"
                bg="white"
                borderBottomRadius="xl"
              >
                <Text
                  fontWeight="bold"
                  fontSize="md"
                  noOfLines={1}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  mb={0}
                >
                  {rv?.vehicle_name}
                </Text>
                {rv?.rating ? (
                  <HStack spacing={1}>
                    <StarIcon boxSize={3} color="#3A4135" />
                    <Text fontWeight="bold" fontSize="sm" mb={0}>
                      {rv?.rating.toFixed(1)}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mb={0}>
                      ({rv?.reviews} reviews)
                    </Text>
                  </HStack>
                ) : (
                  <Text color="gray.500" fontSize="sm" mb={0}>
                    <Translate text="No reviews available" />
                  </Text>
                )}
                <HStack spacing={1} alignItems="flex-start">
                  <Text fontWeight="bold" fontSize="sm" color="gray.800" mb={0}>
                    {/* ${rv?.price_per_day.toLocaleString()} */}
                    {currency} {convertedPrice}
                  </Text>
                  <Text fontSize="sm" color="gray.800" mb={0}>
                    / <Translate text="day" />
                  </Text>
                </HStack>
                <ExternalLinkButton
                  as="a"
                  href={rv?.booking_url}
                  mt={2}
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  bg="#286328"
                  _hover="none"
                  _active="none"
                  textColor="white"
                  width="full"
                >
                  <Translate text=" Open" />
                </ExternalLinkButton>
              </VStack>
            </Box>
          );
        })}
      </HStack>
      {hasNext && (
        <IconButton
          icon={<ArrowForwardIcon />}
          isRound={true}
          size="md"
          onClick={next}
          position="absolute"
          right={{ base: "1px", md: "10px" }}
          backgroundColor="white"
          boxShadow="base"
          zIndex="overlay"
        />
      )}
    </Flex>
  );
};

export default RVRentalCard;
